import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import RoleDashboardPage from "./pages/dashboard/RoleDashboardPage";
import PublicHomePage from "./pages/PublicHomePage";

import Layout from "./components/Layout";
import ClassList from "./pages/ClassList";
import UserCreation from "./pages/UserCreation";
import LoginPage from "./pages/LoginPage";
import CertificationsPage from "./pages/CertificationsPage";
import ClassDetail from "./pages/ClassDetail";
import CourseSubmissionPage from "./pages/CourseSubmissionPage";
import CourseApprovalDashboard from "./pages/CourseApprovalDashboard";
import SemesterListPage from "./pages/SemesterListPage";
import SemesterPlanningPage from "./pages/SemesterPlanningPage";
import ProfilePage from "./pages/ProfilePage";
import TrainingCenterPage from "./pages/TrainingCenterPage";
import EmailLibraryPage from "./pages/EmailLibraryPage";
import InstructorManagementPage from "./pages/InstructorManagementPage";
import AhaIssuedCardsPage from "./pages/AhaIssuedCardsPage";
import RoomManagementPage from "./pages/RoomManagementPage";
import AgencyManagementHub from "./pages/agency/AgencyManagementHub";
import AgencyMemberCompliance from "./pages/agency/AgencyMemberCompliance";
import AgencyClassEnrollments from "./pages/agency/AgencyClassEnrollments";
import AgencyRoleRequirements from "./pages/agency/AgencyRoleRequirements";
import AgencyDetails from "./pages/agency/AgencyDetails";
import AgencyCoiPage from "./pages/agency/AgencyCoiPage";
import EquipmentManagementPage from "./pages/EquipmentManagementPage";
import TranscriptPage from "./pages/TranscriptPage";
import ClassArchivePage from "./pages/ClassArchivePage";
import TenantSetupPage from "./pages/TenantSetupPage";
import AdminUserRolesPage from "./pages/AdminUserRolesPage";
import CourseManagementPage from "./pages/CourseManagementPage";
import ScheduleFromTemplatePage from "./pages/ScheduleFromTemplatePage";
import SkillSheetBankPage from "./pages/banks/SkillSheetBankPage";
import TestBankPage from "./pages/banks/TestBankPage";
import DocumentBankPage from "./pages/banks/DocumentBankPage";
import CourseHubPage from "./pages/courseHub/CourseHubPage";
import CoordinatorCourseDetailPage from "./pages/courseHub/CoordinatorCourseDetailPage";
import SessionIdleWarning from "./components/SessionIdleWarning";
import AttendanceRosterPage from "./pages/courseHub/AttendanceRosterPage";
import InstructorMyCoursesPage from "./pages/instructor/InstructorMyCoursesPage";
import InstructorKnowledgeBankPage from "./pages/instructor/InstructorKnowledgeBankPage";
import MarketplaceView from "./components/knowledgeBank/marketplace/MarketplaceView";
import SkillSheetDetailPage from "./pages/instructor/SkillSheetDetailPage";
import TestDetailPage from "./pages/instructor/TestDetailPage";
import DocumentDetailPage from "./pages/instructor/DocumentDetailPage";
import ChecklistSignOffPage from "./pages/instructor/ChecklistSignOffPage";
import InstructorStudentDashboard from "./pages/instructor/InstructorStudentDashboard";
import InstructorSchedulingPage from "./pages/instructor/InstructorSchedulingPage";
import SkillChecklistAssessPage from "./pages/courseHub/SkillChecklistAssessPage";
import SkillChecklistResultsPage from "./pages/courseHub/SkillChecklistResultsPage";
import TestAssessPage from "./pages/courseHub/TestAssessPage";
import InstructorTestResultsPage from "./pages/courseHub/TestResultsPage";
import StudentTestResultsPage from "./pages/TestResultsPage";
import TestTakePage from "./pages/TestTakePage";
import StudentCourseDashboardPage from "./pages/StudentCourseDashboardPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import CheckinPage from "./pages/checkin/CheckinPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MessagingPage from "./pages/MessagingPage";
import { GLOBAL_ADMIN_PATHS } from "./pages/globalAdmin/paths";
import {
  GlobalAdminTenantsPage,
  GlobalAdminAuditLogPage,
  GlobalAdminSystemHealthPage,
  GlobalAdminTimeTravelPage,
} from "./pages/GlobalAdminPages";
import StandardCoursesPage from "./pages/StandardCoursesPage";
import { useTenant } from "./contexts/TenantContext";

/**
 * Signed-out visitors to an app page (e.g. a link in an email) go to sign in, and LoginPage returns them to the page
 * they asked for (location.state.redirectTo). Only the path and query are kept, so this cannot redirect off-site.
 */
function SignInFirst() {
  const location = useLocation();
  return <Navigate to="/login" replace state={{ redirectTo: location.pathname + location.search }} />;
}

function App() {
  const { user, tenantConfig, isFeatureEnabled } = useTenant();
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const hasAnyRole = (roles) => userRoles.some((role) => roles.includes(role));
  const canManageCourses = hasAnyRole(["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"]);
  const canAccessCourseTools = hasAnyRole(["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN", "INSTRUCTOR"]);
  const canAccessCoordinatorAdminTools = hasAnyRole(["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"]);
  const canManageTenant = hasAnyRole(["GLOBAL_ADMIN"]);
  const showClassPages = isFeatureEnabled
    ? isFeatureEnabled("classes")
    : !!tenantConfig?.features?.classes;
  const showAgencyManagement = isFeatureEnabled
    ? isFeatureEnabled("agency")
    : !!tenantConfig?.features?.agency;
  const showCertManagement = isFeatureEnabled
    ? isFeatureEnabled("certManagement")
    : true;
  const showEquipmentManagement = isFeatureEnabled
    ? isFeatureEnabled("equipmentManagement")
    : !!tenantConfig?.features?.equipmentManagement;

  // set document title based on config
  React.useEffect(() => {
    if (tenantConfig?.name) {
      document.title = tenantConfig.name;
    }
  }, [tenantConfig]);

  // routing is split based on login state.  users who aren't logged in
  // should be able to visit the registration page, so we can't simply
  // return <LoginPage /> early like we used to.  instead the routes
  // below are conditionally rendered.
  return (
    <Layout>
      <SessionIdleWarning />
      <Routes>
        {!user ? (
          // unauthenticated routes
          <>
            <Route path="/users/new" element={<UserCreation />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/magic-link" element={<LoginPage magicLink />} />
            <Route path="/checkin" element={<CheckinPage />} />
            <Route path="/" element={<PublicHomePage />} />
            <Route
              path="/invite/instructor/:token"
              element={<AcceptInvitePage />}
            />
            <Route path="*" element={<SignInFirst />} />
          </>
        ) : (
          // authenticated routes
          <>
            <Route path="/" element={<RoleDashboardPage />} />
            <Route path="/checkin" element={<CheckinPage />} />
            <Route path="/messaging" element={<MessagingPage />} />
            <Route path={GLOBAL_ADMIN_PATHS.tenants} element={<GlobalAdminTenantsPage />} />
            <Route path={GLOBAL_ADMIN_PATHS.auditLog} element={<GlobalAdminAuditLogPage />} />
            <Route path={GLOBAL_ADMIN_PATHS.systemHealth} element={<GlobalAdminSystemHealthPage />} />
            <Route path={GLOBAL_ADMIN_PATHS.timeTravel} element={<GlobalAdminTimeTravelPage />} />
            <Route path="/standard-courses" element={<StandardCoursesPage />} />
            <Route
              path="/invite/instructor/:token"
              element={<AcceptInvitePage />}
            />
            {showClassPages && (
              <>
                <Route path="/classes" element={<ClassList />} />
                <Route path="/classes/:id" element={<ClassDetail />} />
                <Route path="/student-hub/:id" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/documents" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/skill-sheets" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/tests" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/reports" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/virtual-learning" element={<StudentCourseDashboardPage />} />
                <Route path="/student-hub/:id/roster" element={<StudentCourseDashboardPage />} />
                <Route
                  path="/classes/:classId/session/:sessionId/test/:testId"
                  element={<TestTakePage />}
                />
                <Route
                  path="/classes/:classId/session/:sessionId/test/:testId/results"
                  element={<StudentTestResultsPage />}
                />
                <Route path="/classes/:classId/tests/:testId" element={<TestTakePage />} />
                <Route path="/classes/:classId/tests/:testId/results" element={<StudentTestResultsPage />} />
                <Route
                  path="/courses/submit"
                  element={<CourseSubmissionPage />}
                />
                {canAccessCourseTools && (
                  <>
                    {canManageCourses && (
                      <>
                        <Route
                          path="/course-management"
                          element={<CourseManagementPage />}
                        />
                        <Route
                          path="/coordinator/schedule-from-template/:templateId"
                          element={<ScheduleFromTemplatePage />}
                        />
                      </>
                    )}
                    <Route path="/course-hub/:id" element={<CourseHubPage />} />
                    <Route
                      path="/course-hub/:id/details"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/instructors"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/sessions"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/session-content"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/attendance"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/attendance/:sessionId"
                      element={<AttendanceRosterPage />}
                    />
                    <Route
                      path="/course-hub/:id/checklists"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/checklists/:checklistId/assess"
                      element={<SkillChecklistAssessPage />}
                    />
                    <Route
                      path="/course-hub/:id/checklists/:checklistId/assess/:studentId"
                      element={<SkillChecklistAssessPage />}
                    />
                    <Route
                      path="/course-hub/:id/checklists/:checklistId/results/:studentId"
                      element={<SkillChecklistResultsPage />}
                    />
                    <Route
                      path="/course-hub/:id/tests"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/scenarios"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/tests/:testId/assess"
                      element={<TestAssessPage />}
                    />
                    <Route
                      path="/course-hub/:id/tests/:testId/results/:studentId"
                      element={<InstructorTestResultsPage />}
                    />
                    <Route
                      path="/course-hub/:id/reports"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-hub/:id/virtual-learning"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-management/:id"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/details"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/instructors"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/sessions"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/reports"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/skill-sheets"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/documents"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/session-content"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-management/:id/virtual-learning"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/attendance"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-management/:id/attendance/:sessionId"
                      element={<AttendanceRosterPage />}
                    />
                    <Route
                      path="/course-management/:id/checklists"
                      element={<CourseHubPage />}
                    />
                    <Route
                      path="/course-management/:id/checklists/:checklistId/assess"
                      element={<SkillChecklistAssessPage />}
                    />
                    <Route
                      path="/course-management/:id/checklists/:checklistId/assess/:studentId"
                      element={<SkillChecklistAssessPage />}
                    />
                    <Route
                      path="/course-management/:id/checklists/:checklistId/results/:studentId"
                      element={<SkillChecklistResultsPage />}
                    />
                    <Route
                      path="/course-management/:id/tests"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/scenarios"
                      element={<CoordinatorCourseDetailPage />}
                    />
                    <Route
                      path="/course-management/:id/tests/:testId/assess"
                      element={<TestAssessPage />}
                    />
                    <Route
                      path="/course-management/:id/tests/:testId/results/:studentId"
                      element={<InstructorTestResultsPage />}
                    />
                  </>
                )}
                {userRoles.includes("INSTRUCTOR") && (
                  <>
                    <Route
                      path="/instructor/courses"
                      element={<InstructorMyCoursesPage />}
                    />
                    <Route
                      path="/instructor/proposals"
                      element={<InstructorMyCoursesPage defaultTab="proposals" />}
                    />
                    <Route
                      path="/instructor/scheduling"
                      element={<InstructorSchedulingPage />}
                    />
                    <Route
                      path="/instructor/courses/:courseId/students/:studentId"
                      element={<InstructorStudentDashboard />}
                    />
                    <Route
                      path="/instructor/courses/:courseId/checklists/:checklistId/signoff/:studentId"
                      element={<ChecklistSignOffPage />}
                    />
                  </>
                )}
              </>
            )}
            {canAccessCoordinatorAdminTools && (
              <>
                <Route path="/instructor/knowledge-bank" element={<InstructorKnowledgeBankPage />} />
                <Route path="/instructor/knowledge-bank/marketplace" element={<MarketplaceView />} />
                <Route path="/instructor/knowledge-bank/skill-sheets/:id" element={<SkillSheetDetailPage />} />
                <Route path="/instructor/knowledge-bank/tests/:id" element={<TestDetailPage />} />
                <Route path="/instructor/knowledge-bank/documents/:id" element={<DocumentDetailPage />} />
              </>
            )}
            {canAccessCoordinatorAdminTools && (
              <>
                <Route path="/banks/skill-sheets" element={<SkillSheetBankPage />} />
                <Route path="/banks/tests" element={<TestBankPage />} />
                <Route path="/banks/documents" element={<DocumentBankPage />} />
              </>
            )}
            {canAccessCoordinatorAdminTools && (
              <>
                <Route
                  path="/coordinator/approvals"
                  element={<CourseApprovalDashboard />}
                />
                <Route path="/semesters" element={<SemesterListPage />} />
                <Route
                  path="/semesters/:id"
                  element={<SemesterPlanningPage />}
                />
              </>
            )}
            <Route path="/users/new" element={<UserCreation />} />
            <Route path="/profile" element={<ProfilePage />} />
            {showCertManagement && (
              <Route path="/certifications" element={<CertificationsPage />} />
            )}
            {showClassPages && (
              <Route path="/training-center" element={<TrainingCenterPage />} />
            )}
            {showClassPages && (
              <Route path="/email-library" element={<EmailLibraryPage />} />
            )}
            {canManageCourses && (
              <Route
                path="/instructor-management"
                element={<InstructorManagementPage />}
              />
            )}
            {canManageCourses && (
              <Route path="/rooms" element={<RoomManagementPage />} />
            )}
            {canManageCourses && (
              <Route path="/aha-ecards" element={<AhaIssuedCardsPage />} />
            )}
            {(showAgencyManagement ||
              userRoles.some((r) =>
                ["AGENCY_ADMIN", "ADMIN", "GLOBAL_ADMIN"].includes(r),
              )) && (
              <>
                <Route path="/agency" element={<AgencyManagementHub />} />
                <Route
                  path="/agency/member-compliance"
                  element={<AgencyMemberCompliance />}
                />
                <Route
                  path="/agency/class-enrollments"
                  element={<AgencyClassEnrollments />}
                />
                <Route
                  path="/agency/role-requirements"
                  element={<AgencyRoleRequirements />}
                />
                <Route path="/agency/details" element={<AgencyDetails />} />
              </>
            )}
            {/* COI route accessible to AGENCY_ADMIN, ADMIN, GLOBAL_ADMIN, COURSE_COORDINATOR */}
            {userRoles.some((r) =>
              ["AGENCY_ADMIN", "ADMIN", "GLOBAL_ADMIN", "COURSE_COORDINATOR"].includes(r),
            ) && <Route path="/agency/coi" element={<AgencyCoiPage />} />}
            {showEquipmentManagement &&
              (userRoles.includes("AGENCY_ADMIN") ||
                userRoles.includes("ADMIN") ||
                userRoles.includes("GLOBAL_ADMIN")) && (
                <Route
                  path="/equipment"
                  element={<EquipmentManagementPage />}
                />
              )}
            <Route path="/transcript" element={<TranscriptPage />} />
            <Route path="/my-classes/:classId/archive" element={<ClassArchivePage />} />
            {canManageTenant && (
              <Route path="/tenant-setup" element={<TenantSetupPage />} />
            )}
            {(userRoles.includes("ADMIN") || userRoles.includes("GLOBAL_ADMIN")) && (
              <Route
                path="/admin/user-roles"
                element={<AdminUserRolesPage />}
              />
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </Layout>
  );
}

export default App;
